import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAppContext } from '../../../providers/AppProvider';
import { Chart } from 'react-chartjs-2';
import { selectSite } from '../../../store/authReducer';
import { selectMultiSiteSelection } from '../../site-selection/reducer';
import { getOverviewWashCounts } from '../actions';
import moment from 'moment';
import { useIntl } from 'react-intl';
import { selectMyChartsWashCounts } from '../reducer';
import { OVERVIEW_GET_MYCHARTS_WASHCOUNTS } from '../actions';
import { getApiStatus } from '../../../core/utils';
import Loader from '../../../core/components/Loader';

interface WashCountDay {
  date: string;
  count: number;
}

export const DailyWashCountGraph = ({ dayCount = 10 }: { dayCount?: number }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const tz = useAppContext().timeZone;
  const primarySiteId = useSelector(selectSite),
    multiSiteSelection = useSelector(selectMultiSiteSelection);
  const today = moment().format('YYYYMMDD');
  const fromDate = useMemo(
    // fromDate can be 2 - 10 days in the past
    () => moment(today).subtract(Math.min(Math.max(dayCount, 2), 10), 'days'),
    [today, dayCount]
  );
  const overviewWashCounts = useSelector(selectMyChartsWashCounts);
  const overviewWashCountsLoading = useSelector((state) =>
    getApiStatus(state, OVERVIEW_GET_MYCHARTS_WASHCOUNTS)
  );
  const fetchWashCounts = () => {
    dispatch(getOverviewWashCounts(moment.tz(fromDate, tz).toISOString()));
  };

  useEffect(fetchWashCounts, [fromDate, primarySiteId, multiSiteSelection, tz]);

  const chartData: any = useMemo(() => {
    return {
      datasets: [
        {
          type: 'bar',
          label: intl.formatMessage({ id: 'count' }),
          data: overviewWashCounts.map((d: WashCountDay) => d.count),
          backgroundColor: (context: any) => {
            const index = context.dataIndex;
            const count = context.dataset.data.length;
            // Check if the current bar is the last element
            return index === count - 1 ? '#4CA155' : '#3761E3';
          },
          categoryPercentage: 0.85,
          minBarLength: 26,
          color: 'white',
          borderRadius: 6,
          yAxisID: 'y',
          order: 3,
        },
      ],
      labels: overviewWashCounts.map((d: WashCountDay) => moment(d.date).format('ddd')),
    };
  }, [overviewWashCounts]);
  const chartOptions: any = {
    plugins: {
      legend: {
        display: false,
      },
      datalabels: {
        display: true,
        color: '#ffffff', // Color of the text label
        anchor: 'end',
        align: 'bottom',
        font: {
          size: 10,
        },
      },
    },
  };
  return (
    <section className="daily-wash-count overview-chart-card">
      <p className="chart-title">
        {intl.formatMessage({ id: 'carsWashedDays' }, { count: dayCount })}
      </p>
      {overviewWashCountsLoading ? (
        <Loader />
      ) : (
        <Chart type={'bar'} data={chartData} options={chartOptions} />
      )}
    </section>
  );
};
